import { useState, useEffect } from 'react';
import { X, Archive, ArrowRight, AlertCircle, Loader2, Calendar, ArrowLeftRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Retrocession {
  id: string;
  type: 'champagne' | 'ambassadeur' | 'autre';
  description: string;
  amount: number;
  payment_date?: string;
}

interface ClotureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: () => void;
  transactionId: string;
  retrocessions: Retrocession[];
}

export default function ClotureModal({ isOpen, onClose, onValidate, transactionId, retrocessions }: ClotureModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentDates, setPaymentDates] = useState<Record<string, string>>({});

  // Add escape key handler
  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (transactionId) {
      loadExistingDates();
    }
  }, [transactionId]);

  const loadExistingDates = async () => {
    try {
      const { data, error } = await supabase
        .from('transaction_retrocessions')
        .select('id, payment_date')
        .eq('transaction_id', transactionId);

      if (error) throw error;

      const dates: Record<string, string> = {};
      data?.forEach(retro => {
        if (retro.payment_date) {
          dates[retro.id] = retro.payment_date;
        }
      });
      setPaymentDates(dates);
    } catch (err) {
      console.error('Error loading payment dates:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Mettre à jour les dates de paiement des rétrocessions
      for (const [retrocessionId, paymentDate] of Object.entries(paymentDates)) {
        if (paymentDate) {
          const { error: updateError } = await supabase
            .from('transaction_retrocessions')
            .update({ payment_date: paymentDate })
            .eq('id', retrocessionId);

          if (updateError) throw updateError;
        }
      }

      // Mettre à jour le statut de la transaction
      const { error: transactionError } = await supabase
        .from('transactions')
        .update({ status: 'cloture' })
        .eq('id', transactionId);

      if (transactionError) throw transactionError;

      // Ajouter le log de clôture
      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: transactionId,
          user_id: user?.id,
          event_type: 'cloture',
          event_data: {
            retrocessions: retrocessions.map(r => ({
              id: r.id,
              type: r.type,
              amount: r.amount,
              payment_date: paymentDates[r.id]
            }))
          }
        }]);

      onValidate();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full max-w-2xl rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ maxHeight: 'min(92vh, 100dvh)' }}
      >
        {/* ── Header ── */}
        <div className="flex-none bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Archive size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Clôture de la transaction</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Encaissé</span>
                  <ArrowRight size={10} className="text-slate-400" />
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">Clôturé</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5">

          {/* Section rétrocessions */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-rose-100 bg-rose-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center">
                <ArrowLeftRight size={14} className="text-rose-600" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-rose-700">Rétrocessions</h3>
            </div>

            <div className="p-5">
              {retrocessions.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-4">
                  Aucune rétrocession pour cette transaction
                </p>
              ) : (
                <div className="space-y-4">
                  {retrocessions.map((retrocession) => (
                    <div key={retrocession.id} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Type</p>
                          <p className="text-sm font-semibold text-slate-800">{retrocession.type}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Description</p>
                          <p className="text-sm font-semibold text-slate-800">{retrocession.description || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Montant</p>
                          <p className="text-sm font-bold text-rose-700">{formatCurrency(retrocession.amount)}</p>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          Date de paiement
                        </label>
                        <div className="relative">
                          <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                          <input
                            type="date"
                            value={paymentDates[retrocession.id] || ''}
                            onChange={(e) => setPaymentDates(prev => ({
                              ...prev,
                              [retrocession.id]: e.target.value
                            }))}
                            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 bg-white transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-none border-t border-slate-200 bg-white px-6 py-4 flex items-center gap-4">
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm mr-auto">
              <AlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          )}
          <div className="flex gap-3 ml-auto shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-sm"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Clôture en cours…' : 'Clôturer la transaction'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}